/**
 * LeadCaptureModal — shared lead-capture used across the IB Pathway Explorer.
 * Auto-prefills faculty/stream/programme/university context for higher-intent leads.
 * POSTs to existing /api/leads endpoint (same backend as the rest of the site).
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const IB_YEARS = [
  'Pre-IB / MYP', 'IB Year 1 (DP1)', 'IB Year 2 (DP2)',
  'Completed IB', 'IBCP', 'Other curriculum',
];

const COUNTRY_OPTIONS = [
  'Australia', 'UK', 'Canada', 'New Zealand', 'USA',
  'Singapore', 'Ireland', 'Germany', 'Not decided',
];

export const LeadCaptureModal = ({ open, onOpenChange, ctx = {} }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    ibYear: '',
    destination: '',
    consentWhatsApp: true,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => { setForm({ name:'', email:'', phone:'', ibYear:'', destination:'', consentWhatsApp:true }); setDone(false); };

  const subject = ctx.universityName
    ? `${ctx.universityName} — ${ctx.programmeName || ctx.streamName}`
    : ctx.programmeName
      ? `${ctx.programmeName} (${ctx.streamName || ctx.facultyName})`
      : ctx.streamName
        ? `${ctx.streamName} (${ctx.facultyName})`
        : ctx.facultyName || 'IB Pathway Explorer';

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill name, email and phone.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error('Please enter a valid email.'); return; }

    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        destination: form.destination || ctx.country || undefined,
        studyLevel: form.ibYear || undefined,
        fieldOfStudy: subject,
        universityInterest: ctx.universityName || undefined,
        source: 'ib-pathway-explorer',
        consentWhatsApp: form.consentWhatsApp,
        consentEmail: true,
        submitted_at: new Date().toISOString(),
      };
      const r = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setDone(true);
      toast.success("Got it! A counsellor will reach out within 24 hours.");
    } catch (e) {
      toast.error(`Could not submit — ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="ib-lead-modal">
        {done ? (
          <div className="py-8 text-center space-y-3">
            <div className="w-14 h-14 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold">You&apos;re on the list.</h3>
            <p className="text-sm text-gray-500">
              A UniXperts counsellor will reach out within 24 hours to walk you through {' '}
              <span className="font-semibold text-foreground">{subject}</span> in detail.
            </p>
            <Button onClick={() => onOpenChange(false)} className="bg-ux-cyan hover:bg-ux-cyan/90" data-testid="ib-lead-done">Got it</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-ux-cyan uppercase tracking-wider mb-1">
                <Sparkles className="w-3.5 h-3.5" /> Free counselling
              </div>
              <DialogTitle>Talk to a UniXperts IB counsellor</DialogTitle>
              <DialogDescription>
                We&apos;ll personalise this for you in a 30-min call —{' '}
                <span className="font-semibold text-foreground">{subject}</span> + your IB profile + budget + return-to-India plan.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Student / Parent name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid="ib-lead-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" data-testid="ib-lead-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone (WhatsApp) *</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 ..." data-testid="ib-lead-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">IB Year</Label>
                  <select
                    value={form.ibYear} onChange={e => setForm(p => ({ ...p, ibYear: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                    data-testid="ib-lead-year"
                  >
                    <option value="">Select…</option>
                    {IB_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Preferred destination</Label>
                  <select
                    value={form.destination} onChange={e => setForm(p => ({ ...p, destination: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                    data-testid="ib-lead-destination"
                  >
                    <option value="">Select…</option>
                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-500 pt-1">
                <Checkbox
                  checked={form.consentWhatsApp}
                  onCheckedChange={(v) => setForm(p => ({ ...p, consentWhatsApp: !!v }))}
                  data-testid="ib-lead-consent"
                />
                <span>OK to reach me on WhatsApp with personalised study-abroad guidance.</span>
              </label>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Maybe later</Button>
              <Button onClick={submit} disabled={busy} className="bg-ux-cyan hover:bg-ux-cyan/90" data-testid="ib-lead-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Book my free call
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
